import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { useAuth } from '../context/AuthProvider';
import { SEOHead } from '../components/seo/SEOHead';
import { getQRAnalytics } from '../services/sbDynamicQR';
import { BarChart3, Globe, Smartphone, Calendar } from 'lucide-react';

export const DynamicQRAnalyticsPage: React.FC = () => {
  const { shortCode } = useParams<{ shortCode: string }>();
  const { session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState('7d');

  useEffect(() => {
    if (!authLoading && !session) {
      navigate('/account');
    }
  }, [session, authLoading, navigate]);

  const loadAnalytics = useCallback(async () => {
    if (!shortCode) return;

    try {
      setIsLoading(true);
      setError(null);
      const result = await getQRAnalytics(shortCode, period);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  }, [shortCode, period]);

  useEffect(() => {
    if (session && shortCode) {
      loadAnalytics();
    }
  }, [session, shortCode, period, loadAnalytics]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <>
      <SEOHead
        title={`Analytics - ${shortCode} | FreeQRCodeNow`}
        description="View detailed analytics and insights for your dynamic QR code"
      />

      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Analytics</h1>
            {data && (
              <p className="text-muted-foreground mt-1">
                {data.qrCode?.title} ({shortCode})
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border border-input rounded-md px-3 py-2 text-sm"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        ) : !data ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">No data available</p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Scans</p>
                      <p className="text-3xl font-bold">{data.summary.totalScans}</p>
                    </div>
                    <BarChart3 className="h-8 w-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Unique Scans</p>
                      <p className="text-3xl font-bold">{data.summary.uniqueScans}</p>
                    </div>
                    <Smartphone className="h-8 w-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Period</p>
                      <p className="text-3xl font-bold">{data.summary.period}</p>
                    </div>
                    <Calendar className="h-8 w-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Scans by Date */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    Scans Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.charts.scansByDate.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No data</p>
                  ) : (
                    <div className="space-y-2">
                      {data.charts.scansByDate.slice(0, 10).map((item: any) => (
                        <div key={item.date} className="flex justify-between items-center">
                          <span className="text-sm">{item.date}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(
                                    (item.scans / data.summary.totalScans) * 100,
                                    100
                                  )}%`,
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium w-8 text-right">
                              {item.scans}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Scans by Country */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center">
                    <Globe className="h-4 w-4 mr-2" />
                    Top Countries
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.charts.scansByCountry.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No data</p>
                  ) : (
                    <div className="space-y-2">
                      {data.charts.scansByCountry.slice(0, 10).map((item: any) => (
                        <div key={item.country} className="flex justify-between items-center">
                          <span className="text-sm">{item.country}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-green-600 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(
                                    (item.scans / data.summary.totalScans) * 100,
                                    100
                                  )}%`,
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium w-8 text-right">
                              {item.scans}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Scans by Device */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center">
                    <Smartphone className="h-4 w-4 mr-2" />
                    Devices
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data.charts.scansByDevice.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No data</p>
                  ) : (
                    <div className="space-y-2">
                      {data.charts.scansByDevice.map((item: any) => (
                        <div key={item.device} className="flex justify-between items-center">
                          <span className="text-sm capitalize">{item.device}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-purple-600 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(
                                    (item.scans / data.summary.totalScans) * 100,
                                    100
                                  )}%`,
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium w-8 text-right">
                              {item.scans}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Scans by Browser */}
              {data.charts.scansByBrowser && data.charts.scansByBrowser.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center">
                      <Globe className="h-4 w-4 mr-2" />
                      Top Browsers
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {data.charts.scansByBrowser.slice(0, 8).map((item: any) => (
                        <div key={item.browser} className="flex justify-between items-center">
                          <span className="text-sm">{item.browser}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-orange-600 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(
                                    (item.scans / data.summary.totalScans) * 100,
                                    100
                                  )}%`,
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium w-8 text-right">
                              {item.scans}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Scans */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Recent Scans</CardTitle>
                </CardHeader>
                <CardContent>
                  {data.recentScans.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No scans yet</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {data.recentScans.map((scan: any, idx: number) => (
                        <div
                          key={idx}
                          className="text-xs p-2 bg-muted/50 rounded flex justify-between"
                        >
                          <div>
                            <p className="font-medium">
                              {scan.city || 'Unknown'}, {scan.country || 'Unknown'}
                            </p>
                            <p className="text-muted-foreground">
                              {new Date(scan.scanned_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right text-muted-foreground">
                            <div>{scan.device?.browser || 'Unknown'}</div>
                            <div className="text-xs">{scan.device?.device || 'desktop'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </>
  );
};



